import * as React from 'react'
import {
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { cn } from '../../shared/lib/cn'
import { Button } from './button'
import { DataTable, DataTableBadge, type Column, type SortState } from './data-table'
import { Input } from './input'

export type CsvCellValue = string | number | boolean | null | undefined

export interface CsvTableColumn {
  id: string
  header: string
  align?: 'left' | 'center' | 'right'
  width?: string
  minWidth?: string
  sortable?: boolean
  cell?: (value: CsvCellValue, row: CsvRow, rowIndex: number) => React.ReactNode
}

export type CsvRow = Record<string, CsvCellValue>

interface IndexedCsvRow {
  id: string
  index: number
  values: CsvRow
}

export interface CsvTableViewerProps {
  columns: CsvTableColumn[]
  rows: CsvRow[]
  filename?: string
  searchable?: boolean
  exportable?: boolean
  pageSize?: number
  maxHeight?: string
  compact?: boolean
  emptyMessage?: string
  className?: string
  toolbarClassName?: string
  cellClassName?: (value: CsvCellValue, row: CsvRow, column: CsvTableColumn) => string | undefined
}

export function CsvTableViewer({
  columns,
  rows,
  filename = 'table-export.csv',
  searchable = true,
  exportable = true,
  pageSize = 10,
  maxHeight,
  compact = true,
  emptyMessage = 'No rows to display',
  className,
  toolbarClassName,
  cellClassName,
}: CsvTableViewerProps) {
  const [query, setQuery] = React.useState('')
  const [page, setPage] = React.useState(1)
  const [sortState, setSortState] = React.useState<SortState>({ column: null, direction: null })

  const indexedRows = React.useMemo<IndexedCsvRow[]>(
    () => rows.map((row, index) => ({ id: String(index), index, values: row })),
    [rows],
  )

  const filteredRows = React.useMemo(() => {
    const trimmed = query.trim().toLowerCase()
    if (!trimmed) return indexedRows
    return indexedRows.filter(row =>
      columns.some(column => String(row.values[column.id] ?? '').toLowerCase().includes(trimmed)),
    )
  }, [columns, indexedRows, query])

  const sortedRows = React.useMemo(() => {
    if (!sortState.column || !sortState.direction) return filteredRows
    const direction = sortState.direction === 'asc' ? 1 : -1
    return [...filteredRows].sort((left, right) => (
      compareValues(left.values[sortState.column as string], right.values[sortState.column as string]) * direction
    ))
  }, [filteredRows, sortState])

  React.useEffect(() => {
    setPage(1)
  }, [query, rows])

  const tableColumns = React.useMemo<Column<IndexedCsvRow>[]>(() => (
    columns.map((column): Column<IndexedCsvRow> => ({
      id: column.id,
      header: column.header,
      accessorFn: row => row.values[column.id],
      sortable: column.sortable,
      align: column.align,
      width: column.width,
      minWidth: column.minWidth,
      cellClassName: 'align-top',
      cell: ({ row, value }) => (
        <div className={cn('min-w-0 break-words', cellClassName?.(value, row.values, column))}>
          {column.cell ? column.cell(value, row.values, row.index) : formatCell(value)}
        </div>
      ),
    }))
  ), [cellClassName, columns])

  const downloadCsv = () => {
    const csv = [
      columns.map(column => escapeCsv(column.header)).join(','),
      ...sortedRows.map(row =>
        columns.map(column => escapeCsv(row.values[column.id])).join(','),
      ),
    ].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  const showToolbar = searchable || exportable

  return (
    <div className={cn('space-y-3', className)}>
      {showToolbar && (
        <div className={cn('flex flex-wrap items-center justify-between gap-3', toolbarClassName)}>
          {searchable ? (
            <div className="relative min-w-[220px] flex-1 max-w-sm">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Filter rows..."
                className="pl-9"
              />
            </div>
          ) : <div />}

          {exportable && (
            <Button size="sm" variant="outline" onClick={downloadCsv} disabled={sortedRows.length === 0}>
              <ArrowDownTrayIcon />
              CSV
            </Button>
          )}
        </div>
      )}

      <DataTable
        data={sortedRows}
        columns={tableColumns}
        getRowId={(row) => row.id}
        emptyMessage={emptyMessage}
        sortable
        sortState={sortState}
        onSortChange={setSortState}
        paginated={sortedRows.length > pageSize}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        maxHeight={maxHeight}
        compact={compact}
        striped
        hoverable
      />
    </div>
  )
}

function compareValues(left: CsvCellValue, right: CsvCellValue): number {
  if (left == null && right == null) return 0
  if (left == null) return -1
  if (right == null) return 1
  if (typeof left === 'number' && typeof right === 'number') return left - right
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

function formatCell(value: CsvCellValue): React.ReactNode {
  if (value === null || value === undefined || value === '') {
    return <span className="text-gray-400 dark:text-gray-500">-</span>
  }
  if (typeof value === 'boolean') {
    return (
      <DataTableBadge variant={value ? 'success' : 'default'}>
        {value ? 'Yes' : 'No'}
      </DataTableBadge>
    )
  }
  if (typeof value === 'number') {
    return <span className="font-mono tabular-nums">{formatNumber(value)}</span>
  }
  return <span>{value}</span>
}

function formatNumber(value: number): string {
  return Number.isInteger(value)
    ? value.toLocaleString('en-US')
    : value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function escapeCsv(value: CsvCellValue): string {
  const stringValue = value == null ? '' : String(value)
  if (!/[",\n\r]/.test(stringValue)) return stringValue
  return `"${stringValue.replace(/"/g, '""')}"`
}

export default CsvTableViewer
